import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import { maskCep, removeMask } from "../../../../utils/maskUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import Form from "../../../../components/forms/Form";
import { addressFields } from "../../../../constants/forms/addressFields";
import useForm from "../../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditSupplierAddressPage = () => {
  const navigate = useNavigate();
  const { id, addressId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters: addressFilters, pagination: addressPagination } =
    useUrlFilters(
      {
        deleted_at: false,
      },
      `almox_supplierAddresses`,
    );

  // useBaseService para buscar dados e para o putRequest
  const {
    getByColumn: baseServiceGetById,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(addressFields),
    addressFields,
  );

  // --- Query para buscar os detalhes do Endereço ---
  const {
    data: addressData,
    isLoading: isLoadingAddress,
    isError: isErrorAddress,
    error: addressError,
  } = useQueryWithLoader({
    queryKey: ["almox_supplierAddress", id, addressId],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetById(
        entities.suppliers.addresses.getById(id, addressId),
      );
      return response.result;
    },
    staleTime: 5 * 60 * 1000,
  });

  const fetchCepData = useCallback(
      async (value) => {
        if (value && removeMask(value).length === 8) {
          showLoader();
          try {
            const response = await fetch(
              `https://viacep.com.br/ws/${removeMask(value)}/json/`
            );
            if (!response.ok) throw new Error("Erro ao buscar o CEP");
  
            const data = await response.json();
            if (data.erro) throw new Error("CEP não encontrado");
  
            setFormData((prev) => ({
              address: {
                ...prev.address,
                street: data.logradouro || "",
                district: data.bairro || "",
                city: data.localidade || "",
                state: data.estado || "",
                country: "Brasil",
              },
            }));
            showNotification("success", "Endereço preenchido automaticamente!");
          } catch (error) {
            showNotification("error", error.message);
          } finally {
            hideLoader();
          }
        }
      },
      [hideLoader, setFormData, showLoader, showNotification]
    );
  
    useEffect(() => {
      if (formData.address.zip && removeMask(formData.address.zip).length === 8) {
        fetchCepData(formData.address.zip);
      }
    }, [formData.address.zip]);

  useEffect(() => {
    if (!isLoadingAddress && !isErrorAddress && !addressError && addressData) {
      setFormData((prev) => ({
        address: {
          ...prev,
          alias: addressData.alias,
          zip: maskCep(addressData.zip || ""),
          street: addressData.street,
          number: addressData.number,
          district: addressData.district,
          city: addressData.city,
          state: addressData.state,
          country: addressData.country,
          details: addressData.details,
        },
      }));
      hideLoader();
    }
    if (isErrorAddress || addressError) {
      showNotification(
        "error",
        `Erro ao carregar detalhes do endereço: ${
          addressError.response?.data?.message || addressError.message
        }`,
      );
      hideLoader();
      navigate(`/almoxarifado/fornecedores/detalhes/${id}`, {
        replace: true,
        state: {
          type: "error",
          message: "Endereço não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [isLoadingAddress, isErrorAddress, addressError, addressData]);

  // --- Mutação para atualizar o Endereço ---
  const updateAddressMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({}); // Limpa erros de formulário antes de nova tentativa
      const response = await baseServicePut(
        entities.suppliers.supplierAddressUpdate(id, addressId),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        navigate(`/almoxarifado/fornecedores/detalhes/${id}`, {
          state: { type: "success" },
        });
        queryClient.invalidateQueries({
          queryKey: ["almox_supplierAddress", id, addressId],
        }); // Invalida a query do endereço atualizado
        queryClient.invalidateQueries({
          queryKey: [
            "almox_supplierAddresses",
            id,
            addressFilters,
            addressPagination,
          ],
        }); // Invalida a query da lista de endereços
      }
    },
    onError: (err) => {
      console.log("onError ", err);
      if (err && err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleFieldChange = useCallback(
    (fieldId, value) => {
      handleChange(fieldId, value); // Atualiza o estado do formulário
    },
    [handleChange],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const sanitizedData = {
        address: {
          ...cleanedData.address,
          addressable_type: "supplier",
          number: cleanedData.address.number ? String(cleanedData.address.number) : null,
          zip: cleanedData.address.zip ? removeMask(cleanedData.address.zip) : "",
        },
      };
      // Dispara a mutação do TanStack Query
      await updateAddressMutation.mutateAsync(sanitizedData);
    } catch (error) {
      console.log("Erro ao atualizar endereço:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateAddressMutation]);

  const handleBack = useCallback(() => {
    navigate(`/almoxarifado/fornecedores/detalhes/${id}`);
  }, [navigate, id]);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return addressFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleFieldChange}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Endereço do Fornecedor"
        showBackButton={true}
        backUrl={`/almoxarifado/fornecedores/detalhes/${id}/`}
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          textSubmit="Editar"
          textLoadingSubmit="Editando..."
          handleBack={handleBack}
          onSubmit={handleSubmit}
          isLoading={isLoadingAddress || updateAddressMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditSupplierAddressPage;
